import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bookmark, BookmarkCheck, Clock, Plus, X } from "lucide-react";
import { useApi } from "@/lib/api";
import { useTrack } from "@/lib/track";
import { cn } from "@/lib/cn";
import { UrgencyBadge } from "./UrgencyBadge";
import { AddToCollectionMenu } from "@/components/collections/AddToCollectionMenu";

export interface Opportunity {
  id: string;
  kind: string;
  title: string;
  organization: string | null;
  url: string | null;
  description: string | null;
  amount_cents: number | null;
  deadline_at: number | null;
  geo: string | null;
  urgency_score: number | null;
  confidence_score: number | null;
  source_name: string | null;
  source_trust: number | null;
  user_status: "saved" | "pursuing" | "applied" | "won" | "passed" | null;
  /** Pass-5 continuity signals — present when server includes them. */
  is_saved?: number | null;
  user_saved_at?: number | null;
  match_kind?: string | null;
  match_geo?: string | null;
  collection_count?: number | null;
}

interface Props {
  opp: Opportunity;
  variant?: "full" | "compact";
}

/**
 * Premium opportunity card. Calm by default; actions revealed on hover so the
 * card reads as editorial intelligence, not a marketplace listing.
 */
export function OpportunityCard({ opp, variant = "full" }: Props) {
  const api = useApi();
  const qc = useQueryClient();
  const track = useTrack();

  const isSaved = opp.user_status === "saved" || opp.user_status === "pursuing";
  const isPursuing = opp.user_status === "pursuing";

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["opportunities"] });
    qc.invalidateQueries({ queryKey: ["opp-saved"] });
  };

  const save = useMutation({
    mutationFn: () =>
      isSaved
        ? api.del(`/opportunities/${opp.id}/save`)
        : api.post(`/opportunities/${opp.id}/save`),
    onSuccess: () => {
      if (!isSaved) track("opportunity.save", { kind: opp.kind });
      refresh();
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: "pursuing" | "passed") =>
      api.patch(`/opportunities/${opp.id}/status`, { status }),
    onSuccess: (_, status) => {
      if (status === "passed") track("opportunity.dismiss", { kind: opp.kind });
      refresh();
    },
  });

  const follow = useMutation({
    mutationFn: () =>
      api.post(`/opportunities/follows`, {
        topic_kind: "opportunity_kind",
        topic_value: opp.kind,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["opp-follows"] }),
  });

  return (
    <article className="panel p-6 animate-fade-up group">
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <p className="label-eyebrow">
            {opp.kind}
            {opp.organization && <span className="text-muted"> · {opp.organization}</span>}
            {opp.geo && <span className="text-muted"> · {opp.geo}</span>}
          </p>
          <h3 className="mt-2 font-display text-lg leading-snug tracking-tight">
            {opp.url ? (
              <a
                href={opp.url}
                target="_blank"
                rel="noreferrer"
                className="hover:text-gold-glow transition-colors"
              >
                {opp.title}
              </a>
            ) : (
              opp.title
            )}
          </h3>
          {opp.description && variant === "full" && (
            <p className="mt-3 text-sm text-fog/90 leading-relaxed line-clamp-3">
              {opp.description}
            </p>
          )}
        </div>
        <div className="text-right shrink-0 space-y-1 text-[10px] uppercase tracking-[0.18em] text-muted">
          {opp.amount_cents != null && (
            <div className="text-fog tabular-nums">
              ${(opp.amount_cents / 100).toLocaleString()}
            </div>
          )}
          {opp.deadline_at && (
            <div className="tabular-nums text-whisper">
              {new Date(opp.deadline_at).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })}
            </div>
          )}
          <UrgencyBadge deadlineAt={opp.deadline_at} />
        </div>
      </div>

      {continuityLine(opp) && (
        <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-gold-glow/60">
          {continuityLine(opp)}
        </p>
      )}

      <div className="mt-5 pt-4 border-t border-veil/40 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted">
          {opp.source_name && <span>via {opp.source_name}</span>}
          {opp.source_trust != null && (
            <span className="text-whisper"> · trust {opp.source_trust.toFixed(1)}</span>
          )}
          {opp.confidence_score != null && (
            <span className="text-whisper"> · conf {opp.confidence_score.toFixed(2)}</span>
          )}
          {isPursuing && (
            <span className="ml-3 text-gold-glow">Pursuing</span>
          )}
        </div>
        <div
          className={cn(
            "flex items-center gap-1 transition-opacity",
            "opacity-40 group-hover:opacity-100 focus-within:opacity-100",
          )}
        >
          <AddToCollectionMenu kind="opportunity" entryId={opp.id} />
          <ActionRow
            isSaved={isSaved}
            isPursuing={isPursuing}
            onSave={() => save.mutate()}
            onApplyLater={() => setStatus.mutate("pursuing")}
            onDismiss={() => setStatus.mutate("passed")}
            onFollow={() => follow.mutate()}
            busy={save.isPending || setStatus.isPending || follow.isPending}
          />
        </div>
      </div>
    </article>
  );
}

interface ActionRowProps {
  isSaved: boolean;
  isPursuing: boolean;
  onSave: () => void;
  onApplyLater: () => void;
  onDismiss: () => void;
  onFollow: () => void;
  busy: boolean;
}

function ActionRow(props: ActionRowProps) {
  return (
    <div className="flex items-center gap-1">

      <IconBtn
        label={props.isSaved ? "Saved — click to unsave" : "Save"}
        onClick={props.onSave}
        disabled={props.busy}
        active={props.isSaved}
      >
        {props.isSaved ? <BookmarkCheck className="size-4" strokeWidth={1.5} /> : <Bookmark className="size-4" strokeWidth={1.5} />}
      </IconBtn>
      <IconBtn
        label={props.isPursuing ? "Already pursuing" : "Apply later"}
        onClick={props.onApplyLater}
        disabled={props.busy || props.isPursuing}
        active={props.isPursuing}
      >
        <Clock className="size-4" strokeWidth={1.5} />
      </IconBtn>
      <IconBtn label="Follow this kind" onClick={props.onFollow} disabled={props.busy}>
        <Plus className="size-4" strokeWidth={1.5} />
      </IconBtn>
      <IconBtn label="Dismiss" onClick={props.onDismiss} disabled={props.busy}>
        <X className="size-4" strokeWidth={1.5} />
      </IconBtn>
    </div>
  );
}

/**
 * Pick at most one continuity line — never stack reasons. Priority is
 * curated so the most personally-meaningful signal wins:
 *   1. "Worth revisiting" — saved >14d ago, not yet pursued
 *   2. "Saved Nd ago" — recently saved
 *   3. "In {N} of your collections"
 *   4. "Because you follow {kind}"
 *   5. "Relevant to {geo}"
 */
function continuityLine(opp: Opportunity): string | null {
  const isSaved = opp.user_status === "saved" || opp.user_status === "pursuing";
  if (isSaved && opp.user_saved_at) {
    const days = Math.round((Date.now() - opp.user_saved_at) / (24 * 3600 * 1000));
    if (opp.user_status === "saved" && days >= 14) return "Worth revisiting";
    if (days < 1) return "Saved today";
    if (days === 1) return "Saved yesterday";
    if (days < 30) return `Saved ${days} days ago`;
  }
  if ((opp.collection_count ?? 0) > 0) {
    const n = opp.collection_count!;
    return `In ${n} of your collections`;
  }
  if (opp.match_kind) return `Because you follow ${opp.match_kind}`;
  if (opp.match_geo) return `Relevant to ${opp.match_geo}`;
  return null;
}

function IconBtn({
  children,
  label,
  onClick,
  disabled,
  active,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "p-1.5 rounded-md transition-colors",
        active ? "text-gold-glow" : "text-whisper hover:text-fog",
        "disabled:opacity-40 disabled:cursor-not-allowed",
        "hover:bg-veil/40",
      )}
    >
      {children}
    </button>
  );
}
